import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Article {
  id: string;
  reference: string;
  libelle: string;
  unite: string;
  seuil: number;
  quantite: number;
  emplacement: string;
  version: number;
  alerte: boolean;
}
const types: Record<string, string> = {
  IN: 'Entrée',
  OUT: 'Sortie',
  ADJUST: 'Ajustement inventaire',
};
function Historique({ id }: { id: string }) {
  const q = useQuery({
    queryKey: ['stocks', 'historique', id],
    queryFn: () =>
      requete<{
        elements: {
          id: string;
          type: string;
          quantite: number;
          avant: number;
          apres: number;
          motif: string;
          cree_le: string;
        }[];
      }>(`/stocks/${id}/mouvements`),
  });
  return (
    <>
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      {q.isPending && <p>Chargement…</p>}
      <ol>
        {q.data?.elements.map((m) => (
          <li key={m.id}>
            {types[m.type]} : {m.quantite} — solde {m.avant} → {m.apres} — {m.motif} —{' '}
            {new Date(m.cree_le).toLocaleString('fr-FR')}
          </li>
        ))}
      </ol>
    </>
  );
}
export function PageStocks() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [historique, setHistorique] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const liste = useQuery({
    queryKey: ['stocks', 'liste'],
    queryFn: () => requete<{ elements: Article[] }>('/stocks'),
    enabled: peut(contexte, 'stock', 'voir'),
  });
  const interventions = useQuery({
    queryKey: ['interventions-reseau'],
    queryFn: () => requete<{ elements: { id: string; titre: string }[] }>('/interventions-reseau'),
    enabled: peut(contexte, 'stock', 'modifier') && peut(contexte, 'intervention', 'voir'),
  });
  if (!peut(contexte, 'stock', 'voir')) return <p role="alert">Accès aux stocks non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Stocks" />
      {peut(contexte, 'stock', 'creer') && (
        <button onClick={() => setCreation(!creation)}>
          {creation ? 'Fermer le formulaire' : 'Créer un article'}
        </button>
      )}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 700, marginBlock: 20 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setErreur('');
            setEnCours(true);
            try {
              await requete('/stocks', {
                methode: 'POST',
                corps: {
                  reference: d.get('reference'),
                  libelle: d.get('libelle'),
                  unite: d.get('unite'),
                  seuil: Number(d.get('seuil')),
                  emplacement: d.get('emplacement'),
                },
              });
              setCreation(false);
              await client.invalidateQueries({ queryKey: ['stocks'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Référence <input name="reference" required minLength={2} maxLength={60} />
          </label>
          <label>
            Libellé <input name="libelle" required minLength={3} maxLength={200} />
          </label>
          <label>
            Unité <input name="unite" required maxLength={30} defaultValue="u" />
          </label>
          <label>
            Seuil d’alerte{' '}
            <input
              name="seuil"
              type="number"
              min={0}
              max={1000000}
              step={1}
              required
              defaultValue={0}
            />
          </label>
          <label>
            Emplacement <input name="emplacement" maxLength={250} />
          </label>
          <button disabled={enCours}>Enregistrer l’article</button>
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.isPending && <p role="status">Chargement…</p>}
      {liste.data && (
        <p>
          {liste.data.elements.length} article(s) affiché(s), maximum 500. Chaque mouvement est
          conservé dans l’historique.
        </p>
      )}
      {liste.data?.elements.map((a) => (
        <article key={a.id} style={{ borderBottom: '1px solid currentColor', paddingBlock: 20 }}>
          <h2>{a.libelle}</h2>
          <p>
            {a.reference} · {a.emplacement}
          </p>
          <p>
            Stock : {a.quantite} {a.unite} — seuil {a.seuil}
            {a.alerte ? ' — Réapprovisionnement à prévoir' : ''}
          </p>
          {peut(contexte, 'stock', 'modifier') && (
            <form
              key={a.version}
              style={{ display: 'grid', gap: 12, maxWidth: 700 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                setErreur('');
                setEnCours(true);
                try {
                  await requete(`/stocks/${a.id}/mouvements`, {
                    methode: 'POST',
                    corps: {
                      type: d.get('type'),
                      quantite: Number(d.get('quantite')),
                      motif: d.get('motif'),
                      intervention_id: d.get('intervention_id') || null,
                      version: a.version,
                    },
                  });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  await client.invalidateQueries({ queryKey: ['stocks'] });
                  setEnCours(false);
                }
              }}
            >
              <label>
                Mouvement{' '}
                <select name="type">
                  {Object.entries(types).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Quantité (nouveau total pour un ajustement){' '}
                <input name="quantite" type="number" required min={0} max={1000000} step={1} />
              </label>
              <label>
                Motif <input name="motif" required minLength={3} maxLength={500} />
              </label>
              <button disabled={enCours}>Enregistrer le mouvement</button>
              {peut(contexte, 'intervention', 'voir') && (
                <label>
                  Intervention associée (facultative){' '}
                  <select name="intervention_id">
                    <option value="">Aucune</option>
                    {interventions.data?.elements.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.titre}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {interventions.error && <p role="alert">{messageErreur(interventions.error)}</p>}
            </form>
          )}
          <button
            aria-expanded={historique === a.id}
            onClick={() => setHistorique(historique === a.id ? null : a.id)}
          >
            Historique des mouvements
          </button>
          {historique === a.id && <Historique id={a.id} />}
        </article>
      ))}
    </section>
  );
}
